import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { CheckCircle, Globe } from "lucide-react";
import { useTranslation } from "react-i18next"; // Add useTranslation import
import languageLogo from "../assets/Icons/language-icon-matrimony.png";
import image1 from "../assets/img1.jpg";
import image2 from "../assets/img2.jpg";
import image3 from "../assets/img3.jpg";
import image4 from "../assets/img4.jpg";

const MobileLogin = () => {
  const { t, i18n } = useTranslation(); // Initialize useTranslation hook
  const [mobileNumber, setMobileNumber] = useState("");
  const [matrimonyId, setMatrimonyId] = useState("");
  const [verified, setVerified] = useState(false);
  const [showSuccessTick, setShowSuccessTick] = useState(false);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [loginMessage, setLoginMessage] = useState("");
  const [loginError, setLoginError] = useState("");
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowLanguageModal(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const validateForm = () => {
    const errors = {};

    if (!matrimonyId || matrimonyId.trim() === "") {
      errors.common = t("checkCredentials");
    }

    if (!mobileNumber || !/^\d{10}$/.test(mobileNumber.trim())) {
      errors.common = t("checkCredentials");
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    try {
      const response = await fetch(
        "https://backend.newbrindha.com/matrimony/login",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            matrimony_id: matrimonyId,
            phone_number: mobileNumber,
          }),
        },
      );

      const data = await response.json();
      if (data.access_token)
        localStorage.setItem("authToken", data.access_token);

      if (response.ok) {
        setVerified(true);
        setShowSuccessTick(true);
        setLoginMessage(t("loginSuccess"));
        setLoginError("");
        setTimeout(() => navigate("/dashboard"), 1000);
      } else {
        setErrors({ common: t("checkCredentials") });
        setLoginError(t("loginFailed"));
        setLoginMessage("");
      }
    } catch (error) {
      setErrors({ common: t("checkCredentials") });
      setLoginError(t("loginFailed"));
      setLoginMessage("");
    }
  };

  const handleLanguageChange = (selectedLanguage) => {
    i18n.changeLanguage(selectedLanguage); // Use i18n.changeLanguage
    localStorage.setItem("language", selectedLanguage); // Update localStorage key to match i18next config
    setShowLanguageModal(false);
  };

  // useEffect(() => {
  //   const storedLanguage = localStorage.getItem("language");
  //   if (storedLanguage) {
  //     i18n.changeLanguage(storedLanguage); // Set language from localStorage
  //   }
  // }, [i18n]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-x-hidden overflow-y-hidden">
      <div className="absolute inset-0 grid grid-cols-1 sm:grid-cols-4 gap-0 z-0">
        {[image1, image2, image3, image4].map((img, i) => (
          <div
            key={i}
            className="h-full w-full bg-cover bg-center transform transition-transform duration-500 hover:scale-110"
            style={{ backgroundImage: `url(${img})` }}
          />
        ))}
      </div>

      <div ref={dropdownRef} className="absolute top-4 right-4 z-20">
        <button
          onClick={() => setShowLanguageModal(!showLanguageModal)}
          className="bg-white text-[#d35400] p-2 rounded-full font-medium border border-[#f39c12] hover:bg-[#f39c12] hover:text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#f39c12] transition-all duration-150 ease-in-out transform hover:scale-[1.02] shadow-md shadow-[#f39c12]/20 hover:shadow-[#f39c12]/30"
        >
          <img src={languageLogo} alt="language-logo" className="h-8 w-8" />
        </button>

        {showLanguageModal && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg border border-[#f39c12]/20"
          >
            <button
              onClick={() => handleLanguageChange("en")}
              className="w-full text-left px-4 py-2 text-[#d35400] hover:bg-[#f39c12] hover:text-white rounded-t-lg"
            >
              {t("languages.english")}
            </button>
            <button
              onClick={() => handleLanguageChange("ta")}
              className="w-full text-left px-4 py-2 text-[#d35400] hover:bg-[#f39c12] hover:text-white rounded-b-lg"
            >
              {t("languages.tamil")}
            </button>
          </motion.div>
        )}
      </div>

      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white/70 backdrop-blur-sm rounded-3xl shadow-2xl p-6 sm:p-8 w-full max-w-md border border-[#f39c12]/20 relative z-10 mx-4"
      >
        <h2 className="text-2xl font-bold text-center text-[#d35400] mb-4">
          {t("mobileVerification")}
        </h2>
        <div className="space-y-4">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <label className="block text-sm font-medium text-[#d35400]">
              {t("matrimonyId")} <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={matrimonyId}
              onChange={(e) => setMatrimonyId(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base bg-white/70 backdrop-blur-sm"
              placeholder={t("matrimonyIdPlaceholder")}
              required
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <label className="block text-sm font-medium text-[#d35400]">
              {t("enterMobileNumber")} <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={mobileNumber}
              onChange={(e) => {
                const inputValue = e.target.value;
                if (/^\d{0,10}$/.test(inputValue)) {
                  setMobileNumber(inputValue);
                }
              }}
              className="w-full px-3 py-2 border rounded-lg shadow-sm focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base bg-white/70 backdrop-blur-sm"
              placeholder={t("enterMobileNumber")}
              required
            />
          </motion.div>

          {errors.common && (
            <p className="text-red-500 text-sm text-center mt-2">
              {errors.common}
            </p>
          )}

          {loginError && (
            <p className="text-red-500 text-sm mt-2">{loginError}</p>
          )}

          <motion.button
            onClick={handleLogin}
            className="w-full bg-gradient-to-r from-[#f39c12] to-[#d35400] text-white px-6 py-2 rounded-full shadow-lg hover:from-[#f39c12]/90 hover:to-[#d35400]/90 transition-all duration-300 text-sm sm:text-base"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            {t("login")}
          </motion.button>

          {loginMessage && (
            <p className="text-green-500 text-sm mt-2">{loginMessage}</p>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-[#e67e22]">
            {t("alreadyHaveAccount")}{" "}
            <Link
              to="/login"
              className="font-medium text-[#d35400] hover:text-[#f39c12] transition-colors duration-200"
            >
              {t("login")}
            </Link>
          </p>
        </div>
      </motion.div>

      <AnimatePresence>
        {showSuccessTick && (
          <motion.div
            initial={{ opacity: 0, scale: 0 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 flex items-center justify-center bg-transparent backdrop-blur-sm z-50"
          >
            <CheckCircle className="h-20 w-20 text-green-500" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MobileLogin;
